'use client';

import React from 'react';

import { Box, BoxProps } from '../box';
import { Text } from '../text';

export interface ProgressRingProps extends Omit<BoxProps, 'color' | 'size'> {
  /** Progress value (0-100) */
  value: number;
  /** Ring size in pixels */
  size?: number;
  /** Ring thickness in pixels */
  thickness?: number;
  /** Progress color */
  progressColor?: string;
  /** Track color (background) */
  trackColor?: string;
  /** Whether to show percentage label */
  showLabel?: boolean;
  /** Custom label content */
  label?: React.ReactNode;
  /** Label position */
  labelPosition?: 'center' | 'bottom';
  /** Whether to animate the progress */
  animate?: boolean;
  /** Animation duration in seconds */
  animationDuration?: string;
}

/**
 * ProgressRing component - displays progress as a circular ring
 * Perfect for showing completion percentages or loading states
 */
export const ProgressRing: React.FC<ProgressRingProps> = ({
  value,
  size = 120,
  thickness = 12,
  progressColor = '#3182ce',
  trackColor = 'var(--chakra-colors-bg-muted)',
  showLabel = true,
  label,
  labelPosition = 'center',
  animate = true,
  animationDuration = '0.5s',
  ...boxProps
}) => {
  // Ensure value is between 0 and 100
  const normalizedValue = Math.max(0, Math.min(100, value));

  // Calculate dimensions
  const center = size / 2;
  const radius = center - thickness / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (normalizedValue / 100) * circumference;

  // Animation style
  const animationStyle = animate
    ? {
        transition: `stroke-dashoffset ${animationDuration} ease-in-out`,
      }
    : {};

  return (
    <Box
      position="relative"
      display="inline-flex"
      flexDirection="column"
      alignItems="center"
      {...boxProps}
    >
      <Box position="relative" width={size} height={size}>
        <Box as="svg" width={size} height={size} transform="rotate(-90deg)">
          {/* Background circle (track) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={trackColor}
            strokeWidth={thickness}
          />

          {/* Progress circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={thickness}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            style={animationStyle}
          />
        </Box>

        {/* Center label */}
        {showLabel && labelPosition === 'center' && (
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            textAlign="center"
          >
            {label || (
              <Text fontSize="xl" fontWeight="bold" color="fg.default">
                {Math.round(normalizedValue)}%
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* Bottom label */}
      {showLabel && labelPosition === 'bottom' && (
        <Box marginTop={2} textAlign="center">
          {label || (
            <Text fontSize="md" fontWeight="medium" color="fg.default">
              {Math.round(normalizedValue)}%
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProgressRing;
